import React, { useCallback, useState } from 'react';

import CheckIcon from '@mui/icons-material/Check';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  CircularProgress,
  IconButton,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { alpha } from '@mui/material/styles';

import VoucherPublicPreviewCard from '~/components/Vouchers/VoucherPublicPreviewCard';

import type { PreviewPublicVoucher, PreviewVoucherTextReplacement } from '~/services/PromoService';

type Props = {
  localError: string;
  isError: boolean;
  errorHeadline: string;
  errorDetailBlock: string;
  publicVoucher?: PreviewPublicVoucher;
  textReplacements?: Array<PreviewVoucherTextReplacement>;
  isPending?: boolean;
  emptyMessage: string;
  loadingMessage?: string;
};

export default function VoucherPreviewContent({
  localError,
  isError,
  errorHeadline,
  errorDetailBlock,
  publicVoucher,
  textReplacements = [],
  isPending = false,
  emptyMessage,
  loadingMessage,
}: Props) {
  const [copiedToken, setCopiedToken] = useState<string | null>(null);
  const visibleTextReplacements = textReplacements.filter((replacement) => replacement.name.trim());
  const handleCopyTextReplacementToken = useCallback(async (replacementName: string) => {
    const token = `<${replacementName.trim()}>`;
    await navigator.clipboard.writeText(token);
    setCopiedToken(token);
    window.setTimeout(() => setCopiedToken((current) => (current === token ? null : current)), 2000);
  }, []);

  return (
    <Stack spacing={2}>
      {localError ? <Alert severity="warning">{localError}</Alert> : null}
      {isError ? (
        <Alert severity="error">
          <Typography variant="body2">{errorHeadline}</Typography>
          {errorDetailBlock ? (
            <Box component="pre" sx={{ whiteSpace: 'pre-wrap', fontFamily: 'inherit', m: 0, mt: 1 }}>
              {errorDetailBlock}
            </Box>
          ) : null}
        </Alert>
      ) : null}
      <Box sx={{ position: 'relative', minHeight: publicVoucher ? undefined : 72 }}>
        {publicVoucher ? <VoucherPublicPreviewCard voucher={publicVoucher} /> : null}
        {!publicVoucher && !localError && !isError && !isPending ? (
          <Typography variant="body2" color="text.secondary">
            {emptyMessage}
          </Typography>
        ) : null}
        {isPending && loadingMessage ? (
          <Box
            sx={(theme) => ({
              position: publicVoucher ? 'absolute' : 'relative',
              inset: publicVoucher ? 0 : undefined,
              zIndex: 1,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              minHeight: publicVoucher ? undefined : 72,
              borderRadius: publicVoucher ? 7 : 1,
              bgcolor: alpha(theme.palette.background.paper, 0.72),
              backdropFilter: publicVoucher ? 'blur(1px)' : undefined,
            })}
          >
            <Stack direction="row" spacing={1.5} alignItems="center">
              <CircularProgress size={20} />
              <Typography variant="body2" color="text.secondary">
                {loadingMessage}
              </Typography>
            </Stack>
          </Box>
        ) : null}
      </Box>
      {visibleTextReplacements.length > 0 ? (
        <Accordion defaultExpanded disableGutters variant="outlined" sx={{ maxWidth: 760 }}>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Box>
              <Typography variant="subtitle2">Text replacements</Typography>
              <Typography variant="body2" color="text.secondary">
                Replaced in the title, voucher amount display override, description, and details.
              </Typography>
            </Box>
          </AccordionSummary>
          <AccordionDetails sx={{ pt: 0 }}>
            <TableContainer component={Paper} variant="outlined">
              <Table size="small" aria-label="Voucher preview text replacements">
                <TableHead>
                  <TableRow>
                    <TableCell>Replacement key</TableCell>
                    <TableCell>Preview value</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {visibleTextReplacements.map((replacement) => {
                    const replacementToken = `<${replacement.name.trim()}>`;
                    const isCopied = copiedToken === replacementToken;
                    return (
                      <TableRow key={replacement.name}>
                        <TableCell component="th" scope="row" sx={{ width: '36%', overflowWrap: 'anywhere' }}>
                          <Stack direction="row" spacing={0.5} alignItems="center">
                            <Typography
                              component="span"
                              variant="body2"
                              sx={{ fontFamily: 'monospace', overflowWrap: 'anywhere' }}
                            >
                              {replacementToken}
                            </Typography>
                            <IconButton
                              size="small"
                              type="button"
                              onClick={() => handleCopyTextReplacementToken(replacement.name)}
                              aria-label={`Copy ${replacementToken}`}
                              title={isCopied ? 'Copied' : `Copy ${replacementToken}`}
                            >
                              {isCopied ? (
                                <CheckIcon fontSize="small" color="success" />
                              ) : (
                                <ContentCopyIcon fontSize="small" />
                              )}
                            </IconButton>
                          </Stack>
                        </TableCell>
                        <TableCell sx={{ whiteSpace: 'pre-wrap', overflowWrap: 'anywhere' }}>
                          {replacement.value.trim() || ''}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          </AccordionDetails>
        </Accordion>
      ) : null}
    </Stack>
  );
}
